import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { buildSubcontractReportRows, buildExecutiveSummary } from '../lib/subcontractReportData'
import { renderSubcontractReportPdf } from '../lib/subcontractReportPdfRenderer'

const fmt     = n => new Intl.NumberFormat('en-US',{maximumFractionDigits:0}).format(n||0)
const fmtM    = n => { const m=(n||0)/1e6; return m>=1?m.toFixed(1)+'M':fmt(n) }
const today   = () => new Date().toLocaleDateString('en-GB',{day:'2-digit',month:'long',year:'numeric'})
const monthYear = () => new Date().toLocaleDateString('en-GB',{month:'long',year:'numeric'})
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}) : '—'
const GREEN = '#1A7A4A', RED = '#DC2626', NAVY = '#0F1017', AMBER = '#D97706'
// Official CostSpace brand accent — matches ExecutiveReport.jsx's own
// header/logo colors exactly (the report-header identity is shared
// across CostSpace's reports; only GREEN/RED/AMBER, which carry
// semantic meaning here — positive/negative/warning — are project-
// specific to this report and left unchanged).
const BRAND_BLUE = '#1B3A6B', BRAND_BLUE_LIGHT = '#5DADE2'

const STATUS_STYLE = {
  draft:     {bg:'#F3F4F6',color:'#6B7280',label:'Draft'},
  approved:  {bg:'#F0FDF4',color:GREEN,label:'Approved'},
  closed:    {bg:'#EFF6FF',color:'#1D4ED8',label:'Closed'},
  cancelled: {bg:'#FEF2F2',color:RED,label:'Cancelled'},
}
const STATUS_MEANING = {
  draft: 'Not yet approved — no commercial exposure',
  approved: 'Active — ongoing commercial exposure',
  closed: 'Final account settled',
  cancelled: 'Contract cancelled — no commercial exposure',
}

// Copied verbatim from ExecutiveReport.jsx's own ReportLogo — same
// component, so every CostSpace report shares one visual identity
// instead of each report inventing its own logo mark. Supports a
// custom org.logo_url (falls back to the official mark below).
function ReportLogo({ org, size = 44 }) {
  if (org?.logo_url) {
    return (
      <div style={{width:size,height:size,borderRadius:size>32?12:8,overflow:'hidden',background:'#fff',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
        <img src={org.logo_url} alt={org.name||'Logo'} style={{width:'100%',height:'100%',objectFit:'contain'}}/>
      </div>
    )
  }
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" style={{flexShrink:0}}>
      <rect width="100" height="100" rx="24" fill="#1B3A6B"/>
      <path d="M50 18 A32 32 0 1 0 82 50 L68 50 A18 18 0 1 1 50 32 Z" fill="#FFFFFF"/>
      <circle cx="73" cy="50" r="7.5" fill="#5DADE2"/>
    </svg>
  )
}

/**
 * SubcontractReport.jsx
 *
 * Commercial / Cost Control Subcontract Report. Accepts a pre-confirmed
 * scope from SubcontractReportControls.jsx (contractTypes/statuses/
 * contractorIds/sections) and fetches ONLY the data needed for that
 * scope — same tables Subcontracts.jsx's own Financial Summary uses
 * (subcontracts, equipment_contracts, ipc_certificates,
 * cf_payment_entries, retention_releases), plus subcontract_addendums
 * for the Variation Chain (new in this revision).
 *
 * All financial computation is delegated to buildSubcontractReportRows()
 * (subcontractReportData.js), which itself delegates to
 * computeContractFinancials() (contractFinancials.js) — the single
 * unchanged SSOT already used by Subcontracts.jsx. No financial
 * calculation is duplicated or reinvented here.
 *
 * sections.financialPosition is intentionally NOT a separate page — per
 * the approved spec ("Do not create a redundant Payment Summary section
 * if its information is already clearly represented in Financial
 * Position"), certified/paid/outstanding figures already appear in the
 * Contract Summary table (Page 3) and Contractor Detail (Page 6). The
 * toggle controls whether the financial columns show in THOSE sections
 * rather than gating a standalone page — see the `showFinancials` flag
 * derived below.
 *
 * Explicitly excludes subcontract-level Forecast/EAC — confirmed SSOT
 * Gap: forecast_history/buildCumulativeData() does not preserve
 * subcontract_id/po_id at the row level, only sub_code (a text field
 * with no guaranteed 1:1 relationship to a single contract). No
 * inference, no proportional allocation, no fake field.
 */
export default function SubcontractReport({
  projectId, onClose,
  contractTypes = ['material', 'equipment'],
  statuses = ['approved', 'closed'],
  contractorIds = null,
  sections = { executiveSummary: true, contractSummary: true, variations: true, financialPosition: true, retention: true, contractorDetails: true },
}) {
  const [project, setProject] = useState(null)
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState([])

  useEffect(() => { if (projectId) fetchAll(projectId) }, [projectId, JSON.stringify(contractTypes), JSON.stringify(statuses), JSON.stringify(contractorIds)])

  async function fetchAll(pid) {
    setLoading(true)
    const [
      { data: proj },
      { data: subRows },
      { data: eqRows },
      { data: ipcRows },
      { data: cfRows },
      { data: releaseRows },
      { data: subAddendums },
      { data: eqAddendums },
    ] = await Promise.all([
      supabase.from('projects').select('*').eq('id', pid).single(),
      contractTypes.includes('material')
        ? supabase.from('subcontracts').select('*').eq('project_id', pid).order('contract_no')
        : Promise.resolve({ data: [] }),
      contractTypes.includes('equipment')
        ? supabase.from('equipment_contracts').select('*').eq('project_id', pid).order('contract_no')
        : Promise.resolve({ data: [] }),
      supabase.from('ipc_certificates').select('*').eq('project_id', pid).order('period_month'),
      supabase.from('cf_payment_entries').select('amount_inc_vat, po_id').eq('project_id', pid),
      supabase.from('retention_releases').select('subcontract_id, equipment_contract_id, amount').eq('project_id', pid),
      contractTypes.includes('material')
        ? supabase.from('subcontract_addendums').select('*').eq('contract_type', 'material')
        : Promise.resolve({ data: [] }),
      contractTypes.includes('equipment')
        ? supabase.from('subcontract_addendums').select('*').eq('contract_type', 'equipment')
        : Promise.resolve({ data: [] }),
    ])
    setProject(proj)

    let contracts = [
      ...(subRows || []).map(c => ({ ...c, contract_type: 'material' })),
      ...(eqRows || []).map(c => ({ ...c, contract_type: 'equipment' })),
    ]
    contracts = contracts.filter(c => statuses.includes(c.status || 'draft'))
    if (contractorIds) {
      const idSet = new Set(contractorIds)
      contracts = contracts.filter(c => idSet.has(c.id))
    }

    const addendums = [...(subAddendums || []), ...(eqAddendums || [])]

    const reportRows = buildSubcontractReportRows(contracts, ipcRows || [], releaseRows || [], cfRows || [], addendums)
    setRows(reportRows)
    setLoading(false)
  }

  const summary = buildExecutiveSummary(rows)
  const materialCount = rows.filter(r => r.contract_type === 'material').length
  const equipmentCount = rows.filter(r => r.contract_type === 'equipment').length
  const unreconciledRows = rows.filter(r => !r.variation_chain_reconciles)

  const filterSummaryText = [
    `Contract Type: ${contractTypes.map(t => t === 'material' ? 'Material' : 'Equipment').join(', ')}`,
    `Status: ${statuses.map(s => STATUS_STYLE[s]?.label || s).join(', ')}`,
    contractorIds ? `${contractorIds.length} contractor(s) selected` : 'All matching contractors',
  ].join('  ·  ')

  /**
   * Root cause of the earlier blank-preview failure (confirmed): the
   * previous implementation copied the live Modal's rendered
   * `outerHTML` into a new window — DOM cloning of a `position:fixed`
   * overlay, which is fundamentally unreliable for print pagination
   * (this is exactly the "screenshot-based rendering" the approved
   * architecture explicitly rules out).
   *
   * Replaced entirely per the approved architecture: this now calls
   * renderSubcontractReportPdf() (subcontractReportPdfRenderer.js) — a
   * pure, data-driven function that builds a complete, independent A4
   * HTML document directly from the SAME `rows` and `summary` objects
   * already computed for the on-screen React render below. No DOM is
   * read or cloned; no financial value is recalculated; the new window
   * has no relationship to this Modal's position:fixed layout at all,
   * so the browser's native print pagination works correctly.
   */
  function handleOpenPrintWindow() {
    const win = window.open('', '_blank')
    if (!win) return
    const html = renderSubcontractReportPdf(rows, summary, {
      project, contractTypes, statuses, contractorIds, sections,
    })
    win.document.write(html)
    win.document.close()
    win.focus()
    setTimeout(() => win.print(), 400)
  }

  return (
    <div id="sc-report-outer" style={{position:'fixed',inset:0,background:'#CBD5E0',overflowY:'auto',zIndex:9999,fontFamily:"'Inter','IBM Plex Sans',sans-serif"}}>
      <style>{`
        @page {
          size: A4;
          margin: 10mm;
        }
        @media print{
          /* Fallback only — the "Print / Save PDF" button (see
             handleOpenPrintWindow above) now opens a completely
             separate window with its own, simpler print stylesheet
             where this Modal's position:fixed problem doesn't exist.
             This block only applies if someone prints directly from
             this Modal (e.g. browser Ctrl+P) instead of using that
             button — kept as a safety net, not the primary path. */
          body{visibility:hidden}
          #sc-report-outer{position:static!important;inset:auto!important;overflow:visible!important;background:#fff!important}
          #sc-report,#sc-report *{visibility:visible}
          #sc-report{position:static!important;left:auto!important;top:auto!important;width:100%;gap:0!important;padding:0!important;display:block!important}
          .no-print{display:none!important}
          .page{page-break-after:always;page-break-inside:avoid;box-shadow:none!important;margin:0!important;display:block!important;min-height:277mm!important;box-sizing:border-box!important}
          table{page-break-inside:auto}
          tr{page-break-inside:avoid}
          thead{display:table-header-group}
          /* Fixes reported issue: colored backgrounds (navy header,
             colored KPI cards) were printing as plain white — browsers
             strip background colors by default in print to save ink
             unless explicitly told not to. Applied to every element via
             the universal selector since the colored backgrounds are
             inline styles scattered across many different elements
             (page headers, KPI cards, status badges), not one single
             container that could be targeted alone. */
          *{
            -webkit-print-color-adjust:exact!important;
            print-color-adjust:exact!important;
            color-adjust:exact!important;
          }
        }
      `}</style>

      <div className="no-print" style={{position:'sticky',top:0,zIndex:10,background:NAVY,padding:'12px 24px',display:'flex',alignItems:'center',gap:12}}>
        <button onClick={onClose} style={{padding:'8px 16px',background:'rgba(255,255,255,.1)',color:'#fff',border:'none',borderRadius:8,cursor:'pointer',fontSize:13}}>← Back to Controls</button>
        <span style={{color:'rgba(255,255,255,.5)',fontSize:13,flex:1}}>Subcontract Report — {project?.name} — {filterSummaryText}</span>
        <button onClick={handleOpenPrintWindow} style={{padding:'8px 20px',background:GREEN,color:'#fff',border:'none',borderRadius:8,cursor:'pointer',fontSize:13,fontWeight:600}}>🖨 Print / Save PDF</button>
      </div>

      {loading ? (
        <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'60vh'}}>
          <div style={{width:40,height:40,border:'4px solid #E5E7EB',borderTop:`4px solid ${GREEN}`,borderRadius:'50%',animation:'spin 1s linear infinite'}}/>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      ) : rows.length === 0 ? (
        <div style={{maxWidth:600,margin:'80px auto',textAlign:'center',color:'#6B7280',background:'#fff',borderRadius:16,padding:40}}>
          <div style={{fontSize:32,marginBottom:12}}>📭</div>
          <div style={{fontSize:15,fontWeight:700,marginBottom:6}}>No contracts match the selected scope</div>
          <div style={{fontSize:12.5}}>{filterSummaryText}</div>
        </div>
      ) : (
      <div id="sc-report" style={{maxWidth:1100,margin:'24px auto',display:'flex',flexDirection:'column',gap:24,padding:'0 20px 60px'}}>

        {/* PAGE 1 — COVER */}
        <div className="page" style={{background:NAVY,borderRadius:16,overflow:'hidden',minHeight:420}}>
          <div style={{height:4,background:`linear-gradient(90deg,${BRAND_BLUE},${BRAND_BLUE_LIGHT},${BRAND_BLUE})`}}/>
          <div style={{padding:'32px 40px 24px',display:'flex',alignItems:'center',justifyContent:'space-between',borderBottom:'1px solid rgba(255,255,255,.08)'}}>
            <div style={{display:'flex',alignItems:'center',gap:14}}>
              <ReportLogo size={44}/>
              <div>
                <div style={{color:'#fff',fontWeight:800,fontSize:18,letterSpacing:'.04em'}}>Cost<span style={{color:BRAND_BLUE_LIGHT}}>Space</span></div>
                <div style={{color:'rgba(255,255,255,.4)',fontSize:10,letterSpacing:'.12em',textTransform:'uppercase'}}>Enterprise Cost Intelligence</div>
              </div>
            </div>
            <div style={{color:'rgba(255,255,255,.7)',fontSize:12,fontFamily:"'JetBrains Mono',monospace"}}>{today()}</div>
          </div>
          <div style={{padding:'40px 40px 32px',display:'flex',flexDirection:'column',gap:28}}>
            <div>
              <div style={{color:GREEN,fontSize:11,fontWeight:700,letterSpacing:'.15em',textTransform:'uppercase',marginBottom:10}}>Commercial / Cost Control Subcontract Report</div>
              <div style={{color:'#fff',fontSize:38,fontWeight:900,lineHeight:1.1}}>{project?.name}</div>
              <div style={{color:'rgba(255,255,255,.4)',fontSize:14,marginTop:8}}>Original → Variations → Revised · Certified · Retention · Paid · Remaining Commitment</div>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:14}}>
              {[
                {label:'Reporting Period',    value:monthYear()},
                {label:'Contracts in Scope',  value:`${rows.length} (${materialCount} Material · ${equipmentCount} Equipment)`},
                {label:'Contract Types',      value:contractTypes.map(t=>t==='material'?'Material':'Equipment').join(', ')},
                {label:'Status Filter',       value:statuses.map(s=>STATUS_STYLE[s]?.label||s).join(', ')},
              ].map(item=>(
                <div key={item.label} style={{background:'rgba(255,255,255,.05)',borderRadius:10,padding:'14px 18px',border:'1px solid rgba(255,255,255,.08)'}}>
                  <div style={{color:'rgba(255,255,255,.35)',fontSize:10,fontWeight:600,textTransform:'uppercase',letterSpacing:'.1em',marginBottom:5}}>{item.label}</div>
                  <div style={{color:'#fff',fontSize:14,fontWeight:700,fontFamily:"'JetBrains Mono',monospace"}}>{item.value}</div>
                </div>
              ))}
            </div>
            {unreconciledRows.length > 0 && (
              <div style={{background:'rgba(217,119,6,.15)',border:'1px solid rgba(217,119,6,.4)',borderRadius:10,padding:'12px 16px'}}>
                <div style={{color:AMBER,fontSize:12,fontWeight:700}}>⚠ {unreconciledRows.length} contract(s) have a pending Addendum whose linked PO has not yet been re-approved — their Variation Chain total does not yet equal the current contract value. See Variations section for detail.</div>
              </div>
            )}
            <div style={{display:'flex',justifyContent:'space-between',paddingTop:20,borderTop:'1px solid rgba(255,255,255,.08)'}}>
              <div style={{color:'rgba(255,255,255,.25)',fontSize:11}}>🔒 Strictly Confidential · For Top Management Use Only</div>
              <div style={{color:'rgba(255,255,255,.25)',fontSize:11}}>Page 1</div>
            </div>
          </div>
        </div>

        {/* PAGE 2 — EXECUTIVE SUMMARY */}
        {sections.executiveSummary && (
        <div className="page" style={{background:'#fff',borderRadius:16,overflow:'hidden',border:'1px solid #E5E7EB'}}>
          <ReportPageHeader title="Executive Summary" project={project} page={2} />
          <div style={{padding:'24px 32px',display:'flex',flexDirection:'column',gap:20}}>
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12}}>
              {[
                {label:'Total Revised Value',      value:`SAR ${fmtM(summary.totalRevisedValue)}`, color:'#0F1017', bg:'#F4F7FA'},
                {label:'Total Variations',          value:`SAR ${fmtM(summary.totalVariations)}`,   color:'#1D4ED8', bg:'#EFF6FF'},
                {label:'Total Certified',            value:`SAR ${fmtM(summary.totalCertified)}`,     color:GREEN,     bg:'#F0FDF4'},
                {label:'Gross Retention',             value:`SAR ${fmtM(summary.totalRetentionGross)}`, color:AMBER,   bg:'#FFFBEB'},
                {label:'Retention Released',           value:`SAR ${fmtM(summary.totalRetentionReleased)}`, color:'#1D4ED8', bg:'#EFF6FF'},
                {label:'Net Retention Held',            value:`SAR ${fmtM(summary.totalRetentionHeld)}`, color:AMBER,  bg:'#FFFBEB'},
                {label:'Total Paid',                     value:`SAR ${fmtM(summary.totalPaid)}`,        color:'#1D4ED8', bg:'#EFF6FF'},
                {label:'Total Outstanding',               value:`SAR ${fmtM(summary.totalOutstanding)}`, color:summary.totalOutstanding>0?RED:'#6B7280', bg:'#FEF2F2'},
                {label:'Total Remaining Commitment',       value:`SAR ${fmtM(summary.totalRemainingCommitment)}`, color:'#6B7280', bg:'#F9FAFB'},
              ].map(k=>(
                <div key={k.label} style={{background:k.bg,borderRadius:12,padding:'16px',border:'1px solid rgba(0,0,0,.06)'}}>
                  <div style={{fontSize:10,fontWeight:600,textTransform:'uppercase',letterSpacing:'.06em',color:'#6B7280',marginBottom:6}}>{k.label}</div>
                  <div style={{fontSize:17,fontWeight:800,color:k.color,fontFamily:"'JetBrains Mono',monospace"}}>{k.value}</div>
                </div>
              ))}
            </div>
            <div style={{display:'flex',justifyContent:'space-between',paddingTop:8,borderTop:'1px solid #E5E7EB'}}>
              <div style={{fontSize:10,color:'#9CA3AF'}}>Subcontract Report · {project?.name} · {monthYear()}</div>
              <div style={{fontSize:10,color:'#9CA3AF'}}>🔒 Strictly Confidential · Page 2</div>
            </div>
          </div>
        </div>
        )}

        {/* PAGE 3 — CONTRACT SUMMARY TABLE */}
        {sections.contractSummary && (
        <div className="page" style={{background:'#fff',borderRadius:16,overflow:'hidden',border:'1px solid #E5E7EB'}}>
          <ReportPageHeader title="All Contracts — Summary" page={3} project={project} />
          <div style={{padding:'24px 32px',display:'flex',flexDirection:'column',gap:16}}>
            <div style={{borderRadius:12,overflow:'hidden',border:'1px solid #E5E7EB'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:10.5}}>
                <thead>
                  <tr style={{background:NAVY}}>
                    {['Type','Contractor','Contract No.','Revised Value','Certified','Net Retention','Paid','Outstanding','Remaining','Status'].map(h=>(
                      <th key={h} style={{padding:'8px 8px',color:'rgba(255,255,255,.7)',fontWeight:600,fontSize:9,textTransform:'uppercase',textAlign:['Type','Contractor','Contract No.','Status'].includes(h)?'left':'right'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r,i)=>{
                    const st = STATUS_STYLE[r.status] || STATUS_STYLE.draft
                    return (
                      <tr key={r.id} style={{background:i%2===0?'#fff':'#F9FAFB',borderBottom:'1px solid #F3F4F6'}}>
                        <td style={{padding:'6px 8px'}}>{r.contract_type==='equipment'?'🚜':'📦'}</td>
                        <td style={{padding:'6px 8px',fontWeight:600,color:NAVY,maxWidth:140}}>{r.contractor_name}</td>
                        <td style={{padding:'6px 8px',color:'#6B7280',fontFamily:"'JetBrains Mono',monospace"}}>{r.contract_no}</td>
                        <td style={{padding:'6px 8px',textAlign:'right',fontFamily:"'JetBrains Mono',monospace"}}>{fmt(r.revised_value)}</td>
                        <td style={{padding:'6px 8px',textAlign:'right',fontFamily:"'JetBrains Mono',monospace",color:GREEN}}>{fmt(r.certified)}</td>
                        <td style={{padding:'6px 8px',textAlign:'right',fontFamily:"'JetBrains Mono',monospace",color:AMBER}}>{fmt(r.retention_held)}</td>
                        <td style={{padding:'6px 8px',textAlign:'right',fontFamily:"'JetBrains Mono',monospace",color:'#1D4ED8'}}>{fmt(r.paid)}</td>
                        <td style={{padding:'6px 8px',textAlign:'right',fontFamily:"'JetBrains Mono',monospace",color:r.outstanding>0?RED:'#6B7280'}}>{fmt(r.outstanding)}</td>
                        <td style={{padding:'6px 8px',textAlign:'right',fontFamily:"'JetBrains Mono',monospace"}}>{fmt(r.remaining_commitment)}</td>
                        <td style={{padding:'6px 8px'}}><span style={{padding:'2px 8px',borderRadius:20,fontSize:9,fontWeight:700,background:st.bg,color:st.color}}>{st.label}</span></td>
                      </tr>
                    )
                  })}
                  <tr style={{background:'rgba(26,122,74,.06)',borderTop:'2px solid rgba(26,122,74,.2)'}}>
                    <td colSpan={3} style={{padding:'8px 8px',fontWeight:800,color:NAVY}}>Grand Total ({rows.length})</td>
                    <td style={{padding:'8px 8px',textAlign:'right',fontWeight:800,fontFamily:"'JetBrains Mono',monospace"}}>{fmt(summary.totalRevisedValue)}</td>
                    <td style={{padding:'8px 8px',textAlign:'right',fontWeight:800,fontFamily:"'JetBrains Mono',monospace",color:GREEN}}>{fmt(summary.totalCertified)}</td>
                    <td style={{padding:'8px 8px',textAlign:'right',fontWeight:800,fontFamily:"'JetBrains Mono',monospace",color:AMBER}}>{fmt(summary.totalRetentionHeld)}</td>
                    <td style={{padding:'8px 8px',textAlign:'right',fontWeight:800,fontFamily:"'JetBrains Mono',monospace",color:'#1D4ED8'}}>{fmt(summary.totalPaid)}</td>
                    <td style={{padding:'8px 8px',textAlign:'right',fontWeight:800,fontFamily:"'JetBrains Mono',monospace",color:summary.totalOutstanding>0?RED:'#6B7280'}}>{fmt(summary.totalOutstanding)}</td>
                    <td style={{padding:'8px 8px',textAlign:'right',fontWeight:800,fontFamily:"'JetBrains Mono',monospace"}}>{fmt(summary.totalRemainingCommitment)}</td>
                    <td/>
                  </tr>
                </tbody>
              </table>
            </div>
            <div style={{display:'flex',justifyContent:'space-between',paddingTop:8,borderTop:'1px solid #E5E7EB'}}>
              <div style={{fontSize:10,color:'#9CA3AF'}}>Subcontract Report · {project?.name} · {monthYear()}</div>
              <div style={{fontSize:10,color:'#9CA3AF'}}>🔒 Strictly Confidential · Page 3</div>
            </div>
          </div>
        </div>
        )}

        {/* PAGE 4 — VARIATIONS */}
        {sections.variations && (
        <div className="page" style={{background:'#fff',borderRadius:16,overflow:'hidden',border:'1px solid #E5E7EB'}}>
          <ReportPageHeader title="Variation Analysis — by Contract" page={4} project={project} />
          <div style={{padding:'24px 32px',display:'flex',flexDirection:'column',gap:16}}>
            {rows.filter(r => r.variations.length > 0).length === 0 ? (
              <div style={{fontSize:12.5,color:'#9CA3AF',padding:20,textAlign:'center'}}>No variations recorded for any contract in this scope.</div>
            ) : rows.filter(r => r.variations.length > 0).map(r => (
              <div key={r.id} style={{background:'#F9FAFB',borderRadius:12,border:'1px solid #E5E7EB',overflow:'hidden'}}>
                <div style={{background:'rgba(26,122,74,.08)',padding:'10px 16px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                  <span style={{fontWeight:700,color:NAVY,fontSize:12.5}}>{r.contract_type==='equipment'?'🚜':'📦'} {r.contractor_name} ({r.contract_no})</span>
                  {!r.variation_chain_reconciles && (
                    <span style={{fontSize:10,fontWeight:700,color:AMBER,background:'#FFFBEB',padding:'2px 8px',borderRadius:10}}>⚠ Pending PO re-approval</span>
                  )}
                </div>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:10.5}}>
                  <thead>
                    <tr style={{background:'#F3F4F6'}}>
                      {['#','Date','Description','Value Change','New Contract Value'].map(h=>(
                        <th key={h} style={{padding:'6px 10px',color:'#6B7280',fontWeight:600,fontSize:9,textAlign:h==='#'||h==='Date'||h==='Description'?'left':'right'}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{borderBottom:'1px solid #F3F4F6'}}>
                      <td colSpan={3} style={{padding:'6px 10px',fontWeight:600,color:'#6B7280'}}>Original Contract Value</td>
                      <td/>
                      <td style={{padding:'6px 10px',textAlign:'right',fontFamily:"'JetBrains Mono',monospace",fontWeight:700}}>{fmt(r.original_value)}</td>
                    </tr>
                    {r.variations.map(v=>(
                      <tr key={v.addendumNo} style={{borderBottom:'1px solid #F3F4F6'}}>
                        <td style={{padding:'6px 10px',color:'#1D4ED8',fontWeight:700}}>#{v.addendumNo}</td>
                        <td style={{padding:'6px 10px',color:'#6B7280'}}>{fmtDate(v.date)}</td>
                        <td style={{padding:'6px 10px'}}>{v.description}</td>
                        <td style={{padding:'6px 10px',textAlign:'right',fontFamily:"'JetBrains Mono',monospace",color:v.valueChange>=0?GREEN:RED}}>{v.valueChange>=0?'+':''}{fmt(v.valueChange)}</td>
                        <td style={{padding:'6px 10px',textAlign:'right',fontFamily:"'JetBrains Mono',monospace"}}>{fmt(v.newContractValue)}</td>
                      </tr>
                    ))}
                    <tr style={{background:'rgba(26,122,74,.06)',borderTop:'2px solid rgba(26,122,74,.2)'}}>
                      <td colSpan={3} style={{padding:'6px 10px',fontWeight:800,color:NAVY}}>{r.variation_chain_reconciles ? 'Revised Contract Value' : 'Revised (from chain — pending)'}</td>
                      <td style={{padding:'6px 10px',textAlign:'right',fontFamily:"'JetBrains Mono',monospace",fontWeight:700,color:'#1D4ED8'}}>{fmt(r.total_variations)}</td>
                      <td style={{padding:'6px 10px',textAlign:'right',fontFamily:"'JetBrains Mono',monospace",fontWeight:800}}>{fmt(r.variation_chain_expected_revised)}</td>
                    </tr>
                    {!r.variation_chain_reconciles && (
                      <tr>
                        <td colSpan={5} style={{padding:'6px 10px',fontSize:10,color:AMBER,background:'#FFFBEB'}}>
                          ⚠ Current contract value on record is SAR {fmt(r.revised_value)} — differs from the chain total above because the linked PO for the latest addendum has not yet completed re-approval.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ))}
            <div style={{display:'flex',justifyContent:'space-between',paddingTop:8,borderTop:'1px solid #E5E7EB'}}>
              <div style={{fontSize:10,color:'#9CA3AF'}}>Subcontract Report · {project?.name} · {monthYear()}</div>
              <div style={{fontSize:10,color:'#9CA3AF'}}>🔒 Strictly Confidential · Page 4</div>
            </div>
          </div>
        </div>
        )}

        {/* PAGE 5 — RETENTION */}
        {sections.retention && (
        <div className="page" style={{background:'#fff',borderRadius:16,overflow:'hidden',border:'1px solid #E5E7EB'}}>
          <ReportPageHeader title="Retention — Gross / Released / Held" page={5} project={project} />
          <div style={{padding:'24px 32px',display:'flex',flexDirection:'column',gap:16}}>
            <div style={{borderRadius:12,overflow:'hidden',border:'1px solid #E5E7EB'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:10.5}}>
                <thead>
                  <tr style={{background:NAVY}}>
                    {['Contractor','Contract No.','Gross Retention','Released','Net Held','Reconciles'].map(h=>(
                      <th key={h} style={{padding:'8px 10px',color:'rgba(255,255,255,.7)',fontWeight:600,fontSize:9,textTransform:'uppercase',textAlign:h==='Contractor'||h==='Contract No.'?'left':h==='Reconciles'?'center':'right'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r,i)=>{
                    const checkOk = Math.abs((r.retention_gross - r.retention_released) - r.retention_held) < 0.01
                    return (
                      <tr key={r.id} style={{background:i%2===0?'#fff':'#F9FAFB',borderBottom:'1px solid #F3F4F6'}}>
                        <td style={{padding:'6px 10px',fontWeight:600,color:NAVY}}>{r.contract_type==='equipment'?'🚜':'📦'} {r.contractor_name}</td>
                        <td style={{padding:'6px 10px',color:'#6B7280',fontFamily:"'JetBrains Mono',monospace"}}>{r.contract_no}</td>
                        <td style={{padding:'6px 10px',textAlign:'right',fontFamily:"'JetBrains Mono',monospace"}}>{fmt(r.retention_gross)}</td>
                        <td style={{padding:'6px 10px',textAlign:'right',fontFamily:"'JetBrains Mono',monospace",color:'#1D4ED8'}}>{fmt(r.retention_released)}</td>
                        <td style={{padding:'6px 10px',textAlign:'right',fontFamily:"'JetBrains Mono',monospace",color:AMBER,fontWeight:700}}>{fmt(r.retention_held)}</td>
                        <td style={{padding:'6px 10px',textAlign:'center'}}>{checkOk ? '✓' : '⚠'}</td>
                      </tr>
                    )
                  })}
                  <tr style={{background:'rgba(217,119,6,.06)',borderTop:'2px solid rgba(217,119,6,.2)'}}>
                    <td colSpan={2} style={{padding:'8px 10px',fontWeight:800,color:NAVY}}>Grand Total</td>
                    <td style={{padding:'8px 10px',textAlign:'right',fontWeight:800,fontFamily:"'JetBrains Mono',monospace"}}>{fmt(summary.totalRetentionGross)}</td>
                    <td style={{padding:'8px 10px',textAlign:'right',fontWeight:800,fontFamily:"'JetBrains Mono',monospace",color:'#1D4ED8'}}>{fmt(summary.totalRetentionReleased)}</td>
                    <td style={{padding:'8px 10px',textAlign:'right',fontWeight:800,fontFamily:"'JetBrains Mono',monospace",color:AMBER}}>{fmt(summary.totalRetentionHeld)}</td>
                    <td/>
                  </tr>
                </tbody>
              </table>
            </div>
            <div style={{fontSize:10.5,color:'#6B7280'}}>Gross Retention − Released = Net Retention Held, verified per contract (✓/⚠ column above).</div>
            <div style={{display:'flex',justifyContent:'space-between',paddingTop:8,borderTop:'1px solid #E5E7EB'}}>
              <div style={{fontSize:10,color:'#9CA3AF'}}>Subcontract Report · {project?.name} · {monthYear()}</div>
              <div style={{fontSize:10,color:'#9CA3AF'}}>🔒 Strictly Confidential · Page 5</div>
            </div>
          </div>
        </div>
        )}

        {/* PAGE 6 — CONTRACTOR DETAIL */}
        {sections.contractorDetails && (
        <div className="page" style={{background:'#fff',borderRadius:16,overflow:'hidden',border:'1px solid #E5E7EB'}}>
          <ReportPageHeader title="Contractor Detail" page={6} project={project} />
          <div style={{padding:'24px 32px',display:'flex',flexDirection:'column',gap:16}}>
            {rows.map(r => {
              const st = STATUS_STYLE[r.status] || STATUS_STYLE.draft
              return (
                <div key={r.id} style={{background:'#F9FAFB',borderRadius:12,border:'1px solid #E5E7EB',padding:16}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
                    <span style={{fontWeight:800,color:NAVY,fontSize:13.5}}>{r.contract_type==='equipment'?'🚜':'📦'} {r.contractor_name} ({r.contract_no})</span>
                    <span style={{padding:'3px 10px',borderRadius:20,fontSize:10,fontWeight:700,background:st.bg,color:st.color}} title={STATUS_MEANING[r.status]}>{st.label}</span>
                  </div>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,fontSize:10.5}}>
                    <DetailField label="Contract Date" value={fmtDate(r.contract_date)} />
                    <DetailField label="Completion Date" value={fmtDate(r.practical_completion_date)} />
                    <DetailField label="DLP" value={r.dlp_months != null ? `${r.dlp_months} months` : '—'} />
                    <DetailField label="Original Value" value={`SAR ${fmt(r.original_value)}`} />
                    <DetailField label="Variations" value={`SAR ${fmt(r.total_variations)}`} />
                    <DetailField label="Revised Value" value={`SAR ${fmt(r.revised_value)}`} bold />
                    <DetailField label="Certified" value={`SAR ${fmt(r.certified)}`} color={GREEN} />
                    <DetailField label="Paid" value={`SAR ${fmt(r.paid)}`} color="#1D4ED8" />
                    <DetailField label="Gross Retention" value={`SAR ${fmt(r.retention_gross)}`} />
                    <DetailField label="Retention Released" value={`SAR ${fmt(r.retention_released)}`} />
                    <DetailField label="Net Retention Held" value={`SAR ${fmt(r.retention_held)}`} color={AMBER} />
                    <DetailField label="Outstanding" value={`SAR ${fmt(r.outstanding)}`} color={r.outstanding>0?RED:'#6B7280'} />
                    <DetailField label="Remaining Commitment" value={`SAR ${fmt(r.remaining_commitment)}`} />
                  </div>
                </div>
              )
            })}
            <div style={{display:'flex',justifyContent:'space-between',paddingTop:8,borderTop:'1px solid #E5E7EB'}}>
              <div style={{fontSize:10,color:'#9CA3AF'}}>Subcontract Report · {project?.name} · {monthYear()}</div>
              <div style={{fontSize:10,color:'#9CA3AF'}}>🔒 Strictly Confidential · Page 6</div>
            </div>
          </div>
        </div>
        )}

      </div>
      )}
    </div>
  )
}

function ReportPageHeader({ title, page, project }) {
  return (
    <div style={{background:NAVY,padding:'16px 32px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
      <div style={{display:'flex',alignItems:'center',gap:10}}>
        <ReportLogo size={28}/>
        <span style={{color:'#fff',fontWeight:700,fontSize:13}}>Cost<span style={{color:BRAND_BLUE_LIGHT}}>Space</span></span>
        <span style={{color:'rgba(255,255,255,.3)',margin:'0 8px'}}>·</span>
        <span style={{color:'rgba(255,255,255,.6)',fontSize:13}}>{title}</span>
      </div>
      <div style={{color:'rgba(255,255,255,.4)',fontSize:11}}>{monthYear()} · Page {page}</div>
    </div>
  )
}

function DetailField({ label, value, color, bold }) {
  return (
    <div>
      <div style={{color:'#9CA3AF',fontSize:9,textTransform:'uppercase',letterSpacing:'.04em',marginBottom:2}}>{label}</div>
      <div style={{fontFamily:"'JetBrains Mono',monospace",fontWeight:bold?800:600,color:color||'#0F1017'}}>{value}</div>
    </div>
  )
}
